import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, Select, Descriptions, Input } from 'antd';
import { ShoppingOutlined } from '@ant-design/icons';
import type {
  TicketDetailInfo,
  ReportedUserContext,
} from '../../../services/supportTicketsService';

const { Option } = Select;
const { TextArea } = Input;

const STATUS_OPTIONS = [
  { value: 'OPEN', label: 'Mới' },
  { value: 'IN_PROGRESS', label: 'Đang xử lý' },
  { value: 'WAITING_CUSTOMER', label: 'Cần phản hồi' },
  { value: 'RESOLVED', label: 'Đã giải quyết' },
  { value: 'CLOSED', label: 'Đã đóng' },
];

const PRIORITY_OPTIONS = [
  { value: 'LOW', label: 'Thấp' },
  { value: 'MEDIUM', label: 'Trung bình' },
  { value: 'HIGH', label: 'Cao' },
  { value: 'URGENT', label: 'Khẩn cấp' },
];

interface AdminOption {
  id: number;
  name?: string;
  email?: string;
}

interface TicketMetaPanelProps {
  ticket: TicketDetailInfo;
  admins: AdminOption[];
  reportedUserContext?: ReportedUserContext | null;
  onPatch: (patch: {
    status?: string;
    priority?: string;
    assigned_admin_id?: number | null;
  }) => void;
  loading?: boolean;
}

export const TicketMetaPanel: React.FC<TicketMetaPanelProps> = ({
  ticket,
  admins,
  reportedUserContext,
  onPatch,
  loading,
}) => {
  const [internalNotes, setInternalNotes] = useState('');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Card size="small" title="Trạng thái & Gán" loading={loading}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>
              Trạng thái
            </div>
            <Select
              value={ticket.status}
              onChange={v => onPatch({ status: v })}
              style={{ width: '100%' }}
              options={STATUS_OPTIONS}
            />
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>
              Ưu tiên
            </div>
            <Select
              value={ticket.priority}
              onChange={v => onPatch({ priority: v })}
              style={{ width: '100%' }}
              options={PRIORITY_OPTIONS}
            />
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>
              Người xử lý
            </div>
            <Select
              value={ticket.assignedAdmin?.id ?? undefined}
              onChange={v => onPatch({ assigned_admin_id: v ?? null })}
              style={{ width: '100%' }}
              allowClear
              placeholder="Chưa gán"
            >
              {admins.map(a => (
                <Option key={a.id} value={a.id}>
                  {a.name || a.email || `Admin #${a.id}`}
                </Option>
              ))}
            </Select>
          </div>
        </div>
      </Card>

      <Card
        size="small"
        title={
          <span>
            {ticket.source === 'USER' ? 'User' : 'Merchant'}
            {ticket.source && (
              <span
                style={{
                  marginLeft: 8,
                  fontSize: 12,
                  fontWeight: 'normal',
                  color: ticket.source === 'USER' ? '#52c41a' : '#1890ff',
                }}
              >
                ({ticket.source === 'USER' ? 'Khách hàng' : 'Cửa hàng'})
              </span>
            )}
          </span>
        }
      >
        <Descriptions column={1} size="small">
          <Descriptions.Item label="Tên">
            {ticket.merchant?.name || '–'}
          </Descriptions.Item>
          {ticket.source !== 'USER' && (
            <Descriptions.Item label="Cửa hàng">
              {ticket.merchant?.storeName || '–'}
            </Descriptions.Item>
          )}
          <Descriptions.Item label="SĐT">
            {ticket.merchant?.phone || '–'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {ticket.relatedOrderId != null && (
        <Card size="small" title="Đơn hàng liên quan">
          <Link to={`/orders/${ticket.relatedOrderId}`}>
            <ShoppingOutlined /> Đơn #{ticket.relatedOrderId}
          </Link>
        </Card>
      )}

      {String(ticket.category || '').toLowerCase() === 'spam' &&
        reportedUserContext && (
          <Card size="small" title="Khách hàng bị báo cáo (SPAM)">
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Tên">
                <Link to={`/users/${reportedUserContext.id}`}>
                  {reportedUserContext.name || `#${reportedUserContext.id}`}
                </Link>
              </Descriptions.Item>
              <Descriptions.Item label="SĐT">
                {reportedUserContext.phone || '–'}
              </Descriptions.Item>
              <Descriptions.Item label="Email">
                {reportedUserContext.email || '–'}
              </Descriptions.Item>
              <Descriptions.Item label="Ngày tạo TK">
                {reportedUserContext.createdAt
                  ? new Date(reportedUserContext.createdAt).toLocaleString(
                      'vi-VN'
                    )
                  : '–'}
              </Descriptions.Item>
              <Descriptions.Item label="Tổng đơn">
                {reportedUserContext.totalOrders}
              </Descriptions.Item>
              <Descriptions.Item label="Đơn hủy">
                {reportedUserContext.cancelledOrders}
              </Descriptions.Item>
              <Descriptions.Item label="Đơn giao thành công">
                {reportedUserContext.deliveredOrders}
              </Descriptions.Item>
              <Descriptions.Item label="Đơn COD">
                {reportedUserContext.codOrders}
              </Descriptions.Item>
              <Descriptions.Item label="COD hủy (24h)">
                {reportedUserContext.codFailedLast24h}
              </Descriptions.Item>
              <Descriptions.Item label="Trạng thái TK">
                {reportedUserContext.isActive ? 'Active' : 'Inactive / bị khóa'}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        )}

      <Card size="small" title="Ghi chú nội bộ">
        <TextArea
          value={internalNotes}
          onChange={e => setInternalNotes(e.target.value)}
          placeholder="Ghi chú (chưa lưu lên server)..."
          rows={3}
        />
      </Card>

      <Card size="small" title="Nhật ký">
        <div style={{ fontSize: 13, color: '#888' }}>
          Nhật ký thay đổi trạng thái sẽ hiển thị tại đây.
        </div>
      </Card>
    </div>
  );
};
